import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@vr/lib';
import { useSelector } from 'react-redux';
import dashboardColors from '../helpers/dashboardColors';
import cardTypes from '../helpers/cardTypes';
import LogoFlat from '../assets/vrpat/novalogovr.png';
import { JVCEF } from '~/config/constants';
import getCompanyFirstNameByIssuer from '~/helpers/getCompanyFirstNameByIssuer';

const Container = styled.div`
  background-image: ${({ theme, type }) =>
    `url(${dashboardColors(theme, type).bgImage})`};
  background-color: ${({ theme, type }) => dashboardColors(theme, type).color2};
  background-size: cover;
  background-position: -5px;
  width: 311px;
  box-shadow: 0 0 28px 0 rgba(0, 0, 0, 0.35);
  border-radius: 15px;
`;

const BenefitType = styled.p`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 30px;
  background-color: ${({ theme, type, issuer }) =>
    issuer === JVCEF ? 'transparent' : dashboardColors(theme, type).color2};
  color: ${({ theme, type }) => dashboardColors(theme, type).color1};
  font-size: 0.75rem;
  text-transform: capitalize;
  font-weight: 600;
  border-radius: 15px 15px 0 0;
  text-align: ${({ issuer }) => (issuer === JVCEF ? 'left' : 'right')};
  padding-left: ${({ issuer }) => (issuer === JVCEF ? '14px' : 0)};
  padding-right: 12px;
  margin-bottom: 0;
`;
const Body = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: ${({ theme, type, issuer }) =>
    issuer === JVCEF ? 'transparent' : dashboardColors(theme, type).color2};
  color: ${({ theme, type }) => dashboardColors(theme, type).color1};
  padding: 0 14px 12px;
  height: 175px;
  border-radius: 0 0 15px 15px;
`;
const BallanceContainer = styled.div`
  cursor: pointer;
`;
const BallanceAvailable = styled.p`
  padding: 13px 0 11px;
  font-size: 12px;
  font-weight: 500;
`;
const Icon = styled(FontAwesomeIcon)`
  margin-right: 7px;
  font-size: 0.875rem;
`;
const Eye = styled(Icon)`
  font-weight: 700;
`;
const ClickToSee = styled.span`
  text-transform: uppercase;
  font-weight: 700;
`;
const Ballance = styled.p`
  font-size: 2.375rem;
  font-weight: 700;
  width: 255px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const CardInfo = styled.div`
  display: flex;
  justify-content: space-between;
`;
const CardNumber = styled.p`
  font-size: 1rem;
  font-weight: 600;
`;
const CardOwner = styled.p`
  font-size: 0.9375rem;
  font-weight: 500;
  white-space: nowrap;
  width: 230px;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const Logo = styled.img`
  width: 43px;
  height: 43px;
`;
const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;

  p {
    font-size: 0.75rem;
    margin-top: 10px;
  }
  span {
    text-transform: capitalize;
    font-weight: 700;
  }
`;
const Lock = styled(FontAwesomeIcon)`
  font-size: 24px;
  font-weight: 500;
`;
const Exclamation = styled(FontAwesomeIcon)`
  font-size: 70px;
  color: ${({ theme }) => theme.colors.yellow};
`;

function unblockedCard(data, showBallance, setShowBallance) {
  const { saldo = 0, numeroCartao = '0000000000000000', nomeCartao } = data;

  return (
    <>
      <BallanceContainer onClick={() => setShowBallance(!showBallance)}>
        <BallanceAvailable>
          {showBallance && <Icon icon="eye-slash" />}
          {showBallance ? 'Ocultar saldo disponível' : 'Saldo disponível'}
        </BallanceAvailable>
        {!showBallance && (
          <ClickToSee className="balance-step">
            <Eye icon="eye" />
            clique para ver
          </ClickToSee>
        )}
        {showBallance && <Ballance>{saldo.toBRL()}</Ballance>}
      </BallanceContainer>
      <CardInfo>
        <div>
          <CardNumber>{numeroCartao}</CardNumber>
          <CardOwner>{nomeCartao}</CardOwner>
        </div>
        <Logo
          src={LogoFlat}
          alt="Logo flat com fundo verde da VR Benefícios, com VR escrito em branco e centralizado."
        />
      </CardInfo>
    </>
  );
}

export default function Card({ caixa, type, data = {}, blocked, noContent }) {
  const [showBallance, setShowBallance] = useState(true);
  const issuer = useSelector(state => state?.issuer?.name);

  const companyFirstName = getCompanyFirstNameByIssuer(issuer);

  function noContentCard() {
    return (
      <Content>
        <Exclamation icon={['fal', 'exclamation-triangle']} />
      </Content>
    );
  }

  function blockedCard() {
    return (
      <Content>
        <Lock icon="lock" />
        <p>
          Seu cartão{' '}
          <span>
            {companyFirstName} {cardTypes[type]}
          </span>
        </p>
        <p>está bloqueado.</p>
      </Content>
    );
  }

  function renderContent() {
    if (noContent) {
      return noContentCard();
    }
    if (blocked) {
      return blockedCard();
    }
    return unblockedCard(data, showBallance, setShowBallance);
  }

  return (
    <Container type={type}>
      <BenefitType type={caixa ? 'caixa' : type} issuer={issuer}>
        {cardTypes[type]}
      </BenefitType>
      <Body type={caixa ? 'caixa' : type} issuer={issuer}>
        {renderContent()}
      </Body>
    </Container>
  );
}

Card.propTypes = {
  caixa: PropTypes.bool,
  type: PropTypes.number,
  data: PropTypes.object,
  blocked: PropTypes.bool,
  noContent: PropTypes.bool,
};
